import { UserPermissions, UserSubscription, trackUsage } from '@creditsights/api-ui-web';
import { MouseEvent } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { PreviewArticle } from '../components/ArticlePreview/ArticlePreview.logic';
import { ArticleLockType, FeedArticlePermissions } from '../models';

const CREDIT_SIGHTS_LEGACY_URL = 'creditsights.com';
const LEGACY_PLATFORM_URLS = [
  CREDIT_SIGHTS_LEGACY_URL,
  'covenantreview.com',
  'levfininsights.com',
  'capital-structure.com',
];

export const isFromSameSystem = (href: string): boolean => {
  const { hostname, protocol } = window.location;
  const articleMatch = href.match(`${protocol}//${hostname}`);
  return !(articleMatch === null);
};

export const getArticleIdFromHref = (href: string, shortHref: boolean = false): number | undefined => {
  const { hostname, protocol } = window.location;
  const match = shortHref
    ? href.match('/articles/(\\d+)($|\\?.+)')
    : href.match(`${protocol}//${hostname}/articles/(\\d+)($|\\?.+)`);
  return match ? Number(match[1]) : undefined;
};

export const getCompanyIdFromHref = (href: string, shortHref: boolean = false): number | string | undefined => {
  const { hostname, protocol } = window.location;
  const match = shortHref
    ? href.match('/companies/(\\w+)($|\\?.+)')
    : href.match(`${protocol}//${hostname}/companies/(\\w+)($|\\?.+)`);
  return match ? match[1] : undefined;
};

export const getArticleMouseClickHandler =
  (
    onArticleClick: ((article: PreviewArticle, event: MouseEvent<HTMLElement>) => void) | undefined,
    navigate: NavigateFunction
  ) =>
  (e: MouseEvent<HTMLElement>): void => {
    if (!onArticleClick) {
      return;
    }

    const target = e.target as HTMLElement;
    const closestLink = target.closest('A') as HTMLAnchorElement;

    if (!closestLink) {
      return;
    }

    if (isReplaceable(closestLink.href) || isInternalRedirect(closestLink.href)) {
      e.preventDefault();
      const redirectPath = closestLink.pathname.startsWith('/redirect')
        ? closestLink.pathname + closestLink.search
        : `/redirect?url=${closestLink.href}`;
      handleRedirectUrlClick(e.ctrlKey, redirectPath, navigate);
    } else {
      const articleId = getArticleIdFromHref(closestLink.href);
      if (articleId) {
        e.preventDefault();
        onArticleClick({ id: articleId }, e);
      }
    }
  };

export const getArticleLockType = (
  { content, canAccess }: FeedArticlePermissions,
  userPermissions?: UserPermissions
): ArticleLockType => {
  if (
    userPermissions &&
    [UserSubscription.LFI, UserSubscription.CS, UserSubscription.CS_LFI, UserSubscription.RP].includes(
      userPermissions.userSubscription
    ) &&
    !canAccess
  ) {
    return ArticleLockType.Subscribe;
  }
  return content.isPrivate ? ArticleLockType.RequestAccess : ArticleLockType.Subscribe;
};

export const handleRedirectUrlClick = (
  isCtrlClicked: boolean,
  redirectPath: string,
  navigate: NavigateFunction
): void => {
  const redirectUrl = `${redirectPath}&newTab=${isCtrlClicked.toString()}`;
  navigate(redirectUrl, {
    state: {
      from: window.location.href,
    },
  });
};

export const isMail = (link: string): boolean => {
  return link.includes('mailto:');
};
const hasReplaceableUrl = (link: string): boolean => {
  return LEGACY_PLATFORM_URLS.some((item) => link.includes(item) && !link.includes(`know.${CREDIT_SIGHTS_LEGACY_URL}`));
};
export const isReplaceable = (link: string): boolean => {
  return !isFromSameSystem(link) && hasReplaceableUrl(link) && !isMail(link);
};

export const isInternalRedirect = (link: string): boolean => {
  return isFromSameSystem(link) && link.includes('/redirect');
};

export const trackArticleAction = (actionName: string, articleId: number): void => {
  trackUsage('ARTICLE_ACTIONS', [
    {
      key: 'articleId',
      value: String(articleId),
    },
    {
      key: 'name',
      value: actionName,
    },
    {
      key: 'pathName',
      value: `${window.location.pathname}`,
    },
  ]);
};

export const trackArticleView = (viewMode: string, articleId: string): void => {
  trackUsage('ARTICLE_VIEW', [
    {
      key: 'articleId',
      value: articleId,
    },
    {
      key: 'viewMode',
      value: viewMode,
    },
  ]);
};

export const trackArticleAccessDenied = (articleId: string): void => {
  trackUsage('ARTICLE_ACCESS_DENIED', [
    {
      key: 'articleId',
      value: articleId,
    },
  ]);
};
